import { useSyncExternalStore } from "react";
import {
  emailLinkStartResponseSchema,
  inviteLookupResponseSchema,
  teamInvitationLookupResponseSchema,
  signInOptionsResponseSchema,
  signInStartResponseSchema,
  signInVerifyResponseSchema,
  whatsappSignInPollResponseSchema,
  whatsappSignInStartResponseSchema,
  type EmailLinkStartResponse,
  type InviteLookupResponse,
  type TeamInvitationLookupResponse,
  type PhoneSignInChannel,
  type WhatsAppSignInPollRequest,
  type WhatsAppSignInPollResponse,
  type WhatsAppSignInStartResponse,
  type SignInOptionsResponse,
  type SignInStartResponse,
  type SignInVerifyResponse,
} from "@teamna/shared";
import { api } from "../api/client.ts";

/**
 * The session.
 *
 * A mobile number signs in with a one-time code (`POST /v1/auth/start`, then `POST /v1/auth/verify`)
 * or with "Continue with WhatsApp". An email address signs in with a link: `POST
 * /v1/auth/email-link/start` emails it, and the page it opens spends it with `POST
 * /v1/auth/email-link`. Every way ends in the same place: one bearer token in this store.
 *
 * The token lives in localStorage rather than a cookie because the API is a bearer-token API. That
 * also lets the tab that asked for an email link see the session that the link made in another tab
 * (`adoptStoredToken`).
 *
 * A hand-written store rather than a state library: one string, three operations, and
 * `useSyncExternalStore` reads it from React without tearing. The router also reads it outside
 * React, in a route guard, which a hook-only store could not serve.
 */

const TOKEN_KEY = "teamna-web-token";

function readStored(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Safari in private mode throws on any localStorage access. A session that lasts one page load
    // is better than a white screen.
    return null;
  }
}

let token: string | null = typeof window === "undefined" ? null : readStored();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function getToken(): string | null {
  return token;
}

/**
 * True after a sign-in in this tab, until the person picks a place. Opening the app with a stored
 * session is not a sign-in. An account with several places is asked "Where do you want to go?"
 * after every sign-in, and goes to the last place on any other open (decided 2026-10-09).
 */
let freshSignIn = false;

export function isFreshSignIn(): boolean {
  return freshSignIn;
}

export function clearFreshSignIn(): void {
  freshSignIn = false;
}

export function setToken(next: string | null): void {
  if (token === next) return;
  freshSignIn = token === null && next !== null;
  token = next;
  try {
    if (next === null) window.localStorage.removeItem(TOKEN_KEY);
    else window.localStorage.setItem(TOKEN_KEY, next);
  } catch {
    // Kept in memory only. See readStored.
  }
  emit();
}

export function signOut(): void {
  setToken(null);
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The token, as a React value. Re-renders the tree on sign-in and sign-out. */
export function useAuthToken(): string | null {
  return useSyncExternalStore(subscribe, getToken, getToken);
}

export function useIsSignedIn(): boolean {
  return useAuthToken() !== null;
}

/**
 * Takes the session another tab of this site stored, when there is one and this tab has none.
 * The email link opens in a new tab and signs in there; the tab that asked for the link calls this
 * on the `storage` event and moves on too. Answers whether this tab is now signed in.
 */
export function adoptStoredToken(): boolean {
  if (token !== null) return true;
  const stored = readStored();
  if (stored === null) return false;
  token = stored;
  freshSignIn = true;
  emit();
  return true;
}

/** The localStorage key of the session, for a `storage` event listener to match on. */
export const SESSION_STORAGE_KEY = TOKEN_KEY;

/** `GET /v1/auth/options`. Which chip to select first, and whether email is on. */
export async function fetchSignInOptions(): Promise<SignInOptionsResponse> {
  return api.get("/auth/options", signInOptionsResponseSchema);
}

/** The business a join link names, so sign-in can say who the person is joining. Public. */
export async function fetchInvite(code: string): Promise<InviteLookupResponse> {
  return api.get(`/auth/invite?code=${encodeURIComponent(code)}`, inviteLookupResponseSchema);
}

/** What a personal team invitation link shows: the business, the role, the masked number. Public. */
export async function fetchTeamInvitation(invitationToken: string): Promise<TeamInvitationLookupResponse> {
  return api.get(`/auth/team-invitation?token=${encodeURIComponent(invitationToken)}`, teamInvitationLookupResponseSchema);
}

/**
 * Asks for a code. The API decides whether the identifier is a number or an address and answers
 * with how it understood it and which channel the code really went on.
 */
export async function startCode(input: {
  identifier: string;
  channel?: PhoneSignInChannel;
}): Promise<SignInStartResponse> {
  return api.post("/auth/start", input, signInStartResponseSchema);
}

/**
 * "Continue with WhatsApp" without a template: a code and a wa.me link with the message ready to
 * send, plus the poll secret only this screen holds.
 */
export async function startWhatsAppSignIn(): Promise<WhatsAppSignInStartResponse> {
  return api.post("/auth/whatsapp-link/start", {}, whatsappSignInStartResponseSchema);
}

/**
 * Asks whether the WhatsApp message arrived. It stores nothing: the waiting screen first checks
 * that the answer is for the request it still shows, then calls `acceptWhatsAppSession`. So a late
 * answer for an old request can never sign anyone in.
 */
export async function pollWhatsAppSignIn(input: WhatsAppSignInPollRequest): Promise<WhatsAppSignInPollResponse> {
  return api.post("/auth/whatsapp-link/poll", input, whatsappSignInPollResponseSchema);
}

/** Stores the session of a `signed_in` WhatsApp answer, exactly as `verifyCode` stores its token. */
export function acceptWhatsAppSession(sessionToken: string): void {
  setToken(sessionToken);
}

/**
 * Spends a Control Center magic link (the token from the URL fragment of `/demo-sign-in`), stores
 * the session token, and answers with the demo account it signed in to.
 */
export async function signInWithDemoLink(linkToken: string): Promise<SignInVerifyResponse> {
  const result = await api.post("/auth/demo-link", { token: linkToken }, signInVerifyResponseSchema);
  setToken(result.token);
  return result;
}

/** Emails a one-time sign-in link to this address. A waiting invite code goes in the link. */
export async function startEmailLink(input: { email: string; joinCode?: string }): Promise<EmailLinkStartResponse> {
  return api.post("/auth/email-link/start", input, emailLinkStartResponseSchema);
}

/**
 * Spends an emailed sign-in link (the token from the URL fragment of `/email-sign-in`), stores the
 * session token, and answers with the account.
 */
export async function signInWithEmailLink(linkToken: string): Promise<SignInVerifyResponse> {
  const result = await api.post("/auth/email-link", { token: linkToken }, signInVerifyResponseSchema);
  setToken(result.token);
  return result;
}

/** Spends the code, stores the session token, and answers with the account. */
export async function verifyCode(input: { identifier: string; code: string }): Promise<SignInVerifyResponse> {
  const result = await api.post("/auth/verify", input, signInVerifyResponseSchema);
  setToken(result.token);
  return result;
}
